import { ThemeProvider, Toaster, TooltipProvider } from "@disasterscience/scaffoldy";
import * as Sentry from "@sentry/react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { useEffect } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useRegisterSW } from "virtual:pwa-register/react";
import { InitHandlebarsHelpers } from "./components/map/HandlebarsTemplate";
import { ReactFallbackError } from "./components/ReactFallbackError";
import { useAppContext } from "./contexts/AppContext";
import { PaymentProvider } from "./contexts/PaymentContext";
import { SearchProvider } from "./contexts/SearchContext";
import { WebsocketProvider } from "./contexts/WebsocketContext";
import { CapacitorIntegration } from "./lib/capacitor";
import { DEBUG_MODE, firebase, serviceWorkerUpdateInterval } from "./lib/client";
import { ForegroundMessaging } from "./lib/messaging/foreground";
import { Urls } from "./lib/urls";
import { VisibilityStates } from "./lib/visibility";
import { Root } from "./Root";
import "./style/global.css";

export const App = () => {
    const {
        sentryConfig,
        currentDevice,
        urlShortenerApi,
        user,
        notificationApi,
        notificationsShouldBeActive,
        setNotificationsShouldBeActive,
        syncLocalNotifications,
    } = useAppContext();

    useEffect(() => {
        if (!sentryConfig) {
            // Enterprise configs omit sentry; tear down a client started pre-sign-in
            if (Sentry.isEnabled()) {
                if (DEBUG_MODE) console.info("App::useEffect sentry disabling");
                Sentry.close();
            }
            return;
        }

        if (Sentry.isEnabled()) {
            return;
        }

        if (DEBUG_MODE) console.info("App::useEffect sentry initialising", sentryConfig);

        Sentry.init({
            dsn: sentryConfig.dsn,
            integrations: [
                Sentry.browserTracingIntegration(),
                Sentry.browserApiErrorsIntegration({
                    setTimeout: true,
                    setInterval: true,
                    requestAnimationFrame: false,
                    XMLHttpRequest: true,
                    eventTarget: true,
                }),
            ],

            // Performance Monitoring
            // Capture 100% of the transactions
            tracesSampleRate: sentryConfig.tracesSampleRate,

            // Session Replay
            // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
            replaysSessionSampleRate: sentryConfig.replaysSessionSampleRate,

            // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
            replaysOnErrorSampleRate: sentryConfig.replaysOnErrorSampleRate,

            // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
            tracePropagationTargets: sentryConfig.tracePropagationTargets,

            // Environment
            environment: import.meta.env.NODE_ENV.toLowerCase(),
        });
    }, [sentryConfig]);

    useEffect(() => {
        if (DEBUG_MODE) console.info("App::useEffect mapbox prewarming");
        mapboxgl.prewarm();
    }, []);

    // Setup Firebase (only when config becomes available)
    useEffect(() => {
        const firebaseConfig = firebase();

        if (!firebaseConfig) {
            console.log("App::useEffect firebase config not available yet");
            return;
        }

        if (DEBUG_MODE) console.info("App::useEffect firebase config available", firebaseConfig);

        ForegroundMessaging.setupFirebase(firebaseConfig)
            .then((reg) => {
                if (DEBUG_MODE) console.log("App Firebase messaging initialised", !!reg);
            })
            .catch((e) => {
                console.warn("App Firebase messaging not supported, or unable to initialise", e);
            });
    }, []); // Only run once - firebase() returns the same config

    // Keep server token in sync (runs when user or notification state changes)
    useEffect(() => {
        const device = currentDevice();

        if (!user || !notificationApi || notificationsShouldBeActive === true) {
            return;
        }

        if (DEBUG_MODE) console.log("App::useEffect keepServerTokenInSync");

        ForegroundMessaging.keepServerTokenInSync(
            user,
            device,
            notificationsShouldBeActive,
            notificationApi
        )
            .then((insync) => {
                setNotificationsShouldBeActive(insync);
                if (DEBUG_MODE) console.log("App Firebase messaging in sync", insync);
            })
            .catch((e) => {
                console.warn("App Unable to sync firebase messaging token", e);
            });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, notificationsShouldBeActive, notificationApi]);

    useEffect(() => {
        if (DEBUG_MODE) console.info("App::useEffect capacitor initialising");

        // Initialise anything capacitor needs to do
        CapacitorIntegration.initialise();

        // Handle visibility changes
        VisibilityStates.initialise();

        CapacitorIntegration.finalise();

        InitHandlebarsHelpers();
    }, []);

    useEffect(() => {
        syncLocalNotifications();
    }, [syncLocalNotifications]);

    useEffect(() => {
        if (DEBUG_MODE) console.info("App::useEffect url shortener initialising");

        // Initialise urls to ease moving around in the app (or from outside links)
        Urls.initialise(urlShortenerApi);
    }, [urlShortenerApi]);

    // ux#438: legacy SW registrations with non-canonical scriptURLs (e.g. http://, www.)
    // fail registration.update() because the fetch is 3xx'd at the edge. Self-heal on boot.
    useEffect(() => {
        if (!("serviceWorker" in navigator)) return;
        const canonical = `${location.origin}/service-worker.js`;
        navigator.serviceWorker
            .getRegistrations()
            .then((regs) => {
                for (const reg of regs) {
                    const scriptURL =
                        reg.active?.scriptURL ??
                        reg.waiting?.scriptURL ??
                        reg.installing?.scriptURL;
                    if (
                        scriptURL &&
                        scriptURL !== canonical &&
                        scriptURL.endsWith("/service-worker.js")
                    ) {
                        if (DEBUG_MODE) console.info("Unregistering stale SW", scriptURL);
                        reg.unregister();
                    }
                }
            })
            .catch((e) => {
                if (DEBUG_MODE) console.warn("Unable to enumerate SW registrations", e);
            });
    }, []);

    useRegisterSW({
        onRegistered(registration) {
            if (!registration) {
                return;
            }
            setInterval(
                () => {
                    registration.update();
                    ForegroundMessaging.updateServiceWorkerRegistration(registration);
                },
                serviceWorkerUpdateInterval() * 60 * 1000
            );
        },
    });

    if (DEBUG_MODE) console.log("App rendering");

    return (
        <ErrorBoundary FallbackComponent={ReactFallbackError}>
            <ThemeProvider>
                <PaymentProvider>
                    <SearchProvider>
                        <WebsocketProvider>
                            <TooltipProvider>
                                <Root />
                                <Toaster closeButton expand visibleToasts={4} />
                            </TooltipProvider>
                        </WebsocketProvider>
                    </SearchProvider>
                </PaymentProvider>
            </ThemeProvider>
        </ErrorBoundary>
    );
};
